import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import IncomeUnitType from '../../types/unit/IncomeUnit';
import Button from '../common/Button/Button';
import CommercialUnitDatePickerField from './CommercialUnitDatePickerField';
import CommercialUnitDropdownField from './CommercialUnitDropdownField';
import CommercialUnitInputField from './CommercialUnitInputField';
import OperatingExpense from './OperatingExpense';
import Rate from './Rate';
import StepDate from './StepDate';
import "./CommercialUnit.css";
import { useReadOnly } from '../../utils/ReadOnlyContext';
import Checkbox from '../common/Checkbox/Checkbox';
import { getFormErrorByName, getFormErrorMessageByName, hasError, useFormErrorContext } from '../../utils/context/FormErrorContext';
import ErrorMessages from '../common/ErrorMessages/ErrorMessages';
import AIRPNumberInput from '../common/AIRPNumberInput/AIRPNumberInput';
import WarningMessages from '../common/WarningMessages/WarningMessages';
import { useFormWarningContext } from '../../utils/context/FormWarningContext';


interface CommercialUnitProps {
    unit: IncomeUnitType,
    onSave: (unit: Record<string, unknown>) => void,
    onCancel: () => void,
    showDeleteBtn: boolean,
    tenantSummaryType: "1" | "2"
}

export default function CommercialUnit(props: CommercialUnitProps): JSX.Element {
    const { i18n } = useTranslation();
    const { t } = useTranslation(['unit', 'common']);
    const currencySymbol = "$";
    const currencyPerSF = currencySymbol + t('headings.perSF');
    // const [changeField, setChangeField] = useState<string | null>(null);
    const [changedFields, setChangedFields] = useState<Array<string>>([]);
    const { isReadOnly } = useReadOnly()
    const { errors, removeError, clearErrors } = useFormErrorContext();
    const { warnings, clearWarnings } = useFormWarningContext();

    const freeRentMonthClassName = hasError(errors, "freeRentMonth") ? "text-align-right table-error" : "text-align-right"

    useEffect(() => {
        clearErrors();
        clearWarnings();
        // eslint-disable-next-line
    }, [])
    const initData = () => {
        const data = {} as Record<string, unknown>;
        for (const [key, value] of Object.entries(props.unit)) {
            if (value == null) {
                data[key] = undefined;
            } else {
                data[key] = value;
            }
        }
        data['init'] = true;
        return data;
    }

    const [formData, setFormData] = useState<Record<string, unknown>>(initData());

    const numberFields = ["netLeasableArea", "freeRentMonth"];
    const checkFields = [
        "includeInsuranceFlag",
        "includeManagementFlag",
        "includeMaintenanceFlag",
        "includeHydroFlag",
        "includeWaterFlag",
        "includeHvacFlag",
        "includePropertyTaxFlag",
        "linkFlag",
        "landLeaseFlag"
    ];
    const currencyFields = [
        "annualRent",
        "currentBaseRent",
        "propertyTaxRecovery",
        "operatingRecovery",
        "tenantImprovement",
        "stepUpRate1",
        "stepUpRate2",
        "stepUpRate3",
        "stepUpRate4",
    ];

    const saveChangedField = (name: string) => {
        if (!changedFields.includes(name)) {
            setChangedFields([...changedFields, name])
        }
    }

    const handleDateChange = (date: Date | null | undefined, name: string) => {
        if (typeof name !== "string") {
            return;
        }

        let fd = {};
        if (date) {
            const dateStr = format(date, "yyyy-MM-dd")
            fd = { ...formData, [name]: dateStr };
        } else {
            fd = { ...formData, [name]: undefined };
        }
        setFormData(fd);
        // setChangeField(name);
        saveChangedField(name);
    };

    const handleCurrencyChange = (value: number | null, name: string) => {
        setFormData({ ...formData, [name]: value });
        saveChangedField(name);
    };

    const formChanged = (e: React.FormEvent<HTMLFormElement>) => {
        const target = e.target as HTMLInputElement;
        const targetName = target.name;
        if (!currencyFields.includes(targetName)) {
            const value = target.value.trim();
            if (targetName === "variables") {
                const selectedOptions = (e.target as HTMLSelectElement).selectedOptions;
                const values = Array.from(selectedOptions, option => ({
                    code: option.value,
                    description: option.text
                }));
                setFormData({ ...formData, [targetName]: values });
                saveChangedField(targetName);
            } else if (checkFields.includes(targetName)) {
                setFormData({ ...formData, [targetName]: target.checked });
                saveChangedField(targetName);
            } else if (numberFields.includes(targetName)) {
                if (value === "") {
                    setFormData({ ...formData, [targetName]: undefined });
                } else {
                    const newValue = value.replaceAll(",", "")
                    setFormData({ ...formData, [targetName]: Number(newValue) });
                }
                saveChangedField(targetName);
            } else if (targetName === "leaseStatusCode" && value === "1") { // "1" is Vacant
                removeError(getFormErrorByName(errors, "tenantName")?.primaryKey as string)
                removeError(getFormErrorByName(errors, "endDate")?.primaryKey as string)
                removeError(getFormErrorByName(errors, "currentBaseRent")?.primaryKey as string)
                removeError(getFormErrorByName(errors, "annualRent")?.primaryKey as string)
                removeError(getFormErrorByName(errors, "freeRentMonth")?.primaryKey as string)
                removeError(getFormErrorByName(errors, "leaseTypeCode")?.primaryKey as string)

                const updatedData = {
                    ...formData,
                    leaseTypeCode: "",
                    tenantName: "Vacant",
                    startDate: undefined, // If Lease Status = Vacant, blank out both Start and End Date
                    negotiatedDate: undefined,
                    endDate: undefined, // If Lease Status = Vacant, blank out both Start and End Date,
                    includeInsuranceFlag: undefined,
                    includeManagementFlag: undefined,
                    includeMaintenanceFlag: undefined,
                    includeHydroFlag: undefined,
                    includeWaterFlag: undefined,
                    includeHvacFlag: undefined,
                    includePropertyTaxFlag: undefined,
                    landLeaseFlag: undefined,
                    annualRent: undefined,
                    currentBaseRent: undefined,
                    propertyTaxRecovery: undefined,
                    operatingRecovery: undefined,
                    freeRentMonth: undefined,
                    tenantImprovement: undefined,
                    stepUpDate1: undefined,
                    stepUpDate2: undefined,
                    stepUpDate3: undefined,
                    stepUpDate4: undefined,
                    stepUpRate1: undefined,
                    stepUpRate2: undefined,
                    stepUpRate3: undefined,
                    stepUpRate4: undefined,
                    analysisFlag: "N",
                    analysisReviewedBy: "SYSTEM",
                    analysisReviewedDate: new Date().toISOString(),
                    [targetName]: value
                };
                setFormData(updatedData);
                setChangedFields([
                    ...changedFields,
                    targetName,
                    "leaseTypeCode",
                    "tenantName",
                    "startDate",
                    "negotiatedDate",
                    "endDate",
                    "includeInsuranceFlag",
                    "includeManagementFlag",
                    "includeMaintenanceFlag",
                    "includeHydroFlag",
                    "includeWaterFlag",
                    "includeHvacFlag",
                    "includePropertyTaxFlag",
                    "landLeaseFlag",
                    "annualRent",
                    "currentBaseRent",
                    "propertyTaxRecovery",
                    "operatingRecovery",
                    "freeRentMonth",
                    "tenantImprovement",
                    "stepUpDate1",
                    "stepUpDate2",
                    "stepUpDate3",
                    "stepUpDate4",
                    "stepUpRate1",
                    "stepUpRate2",
                    "stepUpRate3",
                    "stepUpRate4"]);
            } else if (targetName === "leaseTypeCode" && (value === "1" || (formData["leaseTypeCode"] === "2" && (value === "3" || value === "4")))) {
                const updatedData = {
                    ...formData,
                    includeInsuranceFlag: false,
                    includeManagementFlag: false,
                    includeMaintenanceFlag: false,
                    includeHydroFlag: false,
                    includeWaterFlag: false,
                    includeHvacFlag: false,
                    includePropertyTaxFlag: false,
                    [targetName]: value
                };
                setFormData(updatedData);
                setChangedFields([...changedFields, targetName, "includeInsuranceFlag", "includeManagementFlag", "includeMaintenanceFlag", "includeHydroFlag", "includeWaterFlag", "includeHvacFlag", "includePropertyTaxFlag"]);
            } else if (targetName === "leaseTypeCode" && value === "2") {
                const updatedData = {
                    ...formData,
                    includeInsuranceFlag: true,
                    includeManagementFlag: true,
                    includeMaintenanceFlag: true,
                    includeHydroFlag: true,
                    includeWaterFlag: true,
                    includeHvacFlag: true,
                    includePropertyTaxFlag: true,
                    [targetName]: value
                };
                setFormData(updatedData);
                setChangedFields([...changedFields, targetName, "includeInsuranceFlag", "includeManagementFlag", "includeMaintenanceFlag", "includeHydroFlag", "includeWaterFlag", "includeHvacFlag", "includePropertyTaxFlag"]);
            } else if (targetName === "unitSubtypeCode" && value === "102") {
                const updatedData = {
                    ...formData,
                    floorLevel: "",
                    netLeasableArea: null,
                    [targetName]: value
                };
                setFormData(updatedData);
                setChangedFields([...changedFields, targetName, "floorLevel", "netLeasableArea"]);
            } else {
                if (value === "") {
                    setFormData({ ...formData, [targetName]: undefined });
                } else {
                    setFormData({ ...formData, [targetName]: value });
                }
                saveChangedField(targetName);
            }
            // setChangeField(targetName);
        }
    }

    const submit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        let result = props.unit;
        result.unitTypeCode = "1"



        if (changedFields.includes("analysisFlag")) {
            const analysisReviewedBy = localStorage.getItem("username") ?? "SYSTEM"
            const analysisReviewedDate = (new Date().toISOString()) ?? null
            result.analysisReviewedBy = analysisReviewedBy
            result.analysisReviewedDate = analysisReviewedDate
        }

        changedFields.forEach((field) => {
            const value = formData[field];
            if (value) {
                result = { ...result, [field]: value };
            } else {
                result = { ...result, [field]: null };
            }
        });

        if (result.leaseStatusCode === "1") {
            result.tenantName = "Vacant"
        }

        props.onSave(result as unknown as Record<string, unknown>);
        // }
    }

    return (
        <div>
            <form id="unit-form" onSubmit={(e) => submit(e)} onChange={(e) => formChanged(e)} data-testid="commercial-unit-form">
                <div className='unit-error-section'>
                    <ErrorMessages errors={errors} />
                    <WarningMessages warnings={warnings} />
                </div>
                <table className='unit-table no-border-input' role="none">

                    <tbody className='valign-top'>



                        <tr className='unit-root-level-tr'>
                            <td>
                                <table className='unit-table-section' role="none">

                                    <tbody>
                                        <CommercialUnitInputField name='tenantUnitNumber' formData={formData} errors={errors}
                                            heading={t('headings.unit-number')} title={t('tooltips.unit-number-tooltip')} />
                                        <CommercialUnitDropdownField name='floorLevel' formData={formData} errors={errors}
                                            heading={t('headings.floor-level')} title={t('tooltips.floor-level-tooltip')} required={true}
                                            disabled={isReadOnly || formData.unitSubtypeCode === '102'} />
                                        <CommercialUnitInputField name='netLeasableArea' formData={formData} errors={errors} type="integer"
                                            heading={t('headings.leased-area')} title={t('tooltips.leased-area-tooltip')}
                                            disabled={isReadOnly || formData.unitSubtypeCode === '102'} />
                                        <CommercialUnitDropdownField name='leaseStatusCode' formData={formData} errors={errors}
                                            heading={t('headings.lease-status')} title={t('tooltips.lease-status-tooltip')} required={true} />
                                        <CommercialUnitDropdownField name='leaseTypeCode' formData={formData} errors={errors} ignoreHandleChange={true}
                                            heading={t('headings.lease-type')} title={t('tooltips.lease-type-tooltip')} disabled={formData.leaseStatusCode === '1'} required={formData.leaseStatusCode === '1' ? false : true} />
                                        <CommercialUnitInputField name='tenantName' formData={formData} errors={errors} testId='commercial-unit-tenant-name-input'
                                            heading={t('headings.tenant-name')} title={t('tooltips.tenant-name-tooltip')} disabled={formData.leaseStatusCode === '1'} required={true} />
                                        <CommercialUnitDropdownField name='unitSubtypeCode' formData={formData} errors={errors}
                                            heading={(formData.leaseStatusCode === '1') ? t('headings.former-unit-use') : t('headings.unit-use')} title={t('tooltips.unit-use-tooltip')}
                                            required={true} />
                                    </tbody>
                                </table>
                            </td>
                            <td>
                                <table className='unit-table-section' role="none">
                                    <tbody>
                                        <CommercialUnitDatePickerField name={'negotiatedDate'} formData={formData} errors={errors}
                                            onChange={handleDateChange} asterisk={false}
                                            heading={t('headings.negotiated-date')} title={t('tooltips.negotiated-date-tooltip')} disabled={formData.leaseStatusCode === '1'} />
                                        <CommercialUnitDatePickerField name={'startDate'} formData={formData} errors={errors}
                                            onChange={handleDateChange} asterisk={true} maxDate={new Date().getFullYear() + "-12-31"}
                                            heading={(formData.leaseStatusCode === '1') ? t('headings.vac-start-date') : t('headings.start-date')} title={t('tooltips.start-date-tooltip')} />
                                        <CommercialUnitDatePickerField name={'endDate'} formData={formData} errors={errors}
                                            onChange={handleDateChange} asterisk={false} minDate={"2019-06-01"}
                                            heading={t('headings.end-date')} title={t('tooltips.end-date-tooltip')} disabled={formData.leaseStatusCode === '1'} />
                                        <tr>
                                            <td className='label-cell'>{t('headings.land-lease')}</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <fieldset className='noborder-fieldset' aria-label="landlease-flag">
                                                    <legend className='invisible-legend'><span>landLeaseFlag</span></legend>
                                                    <div className='land-lease' title={t('tooltips.land-lease-tooltip')}>
                                                        <Checkbox
                                                            onChange={() => removeError(getFormErrorByName(errors, "landLeaseFlag")?.primaryKey as string)}
                                                            name='landLeaseFlag'
                                                            id='landLeaseFlag'
                                                            defaultChecked={formData.landLeaseFlag != null && formData.landLeaseFlag === true}
                                                            checked={formData.landLeaseFlag != null && formData.landLeaseFlag === true}
                                                            disabled={formData.leaseStatusCode === '1'}
                                                        />
                                                    </div>
                                                    <div className='input-error-message'>{getFormErrorMessageByName(errors, "landLeaseFlag")}</div>
                                                </fieldset>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className='label-cell' id="select-all-expenses"
                                                title={t('tooltips.enpenses-header-tooltip')}>
                                                {t('headings.enpenses-header')}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table role="none">
                                    <OperatingExpense formData={formData} disabled={formData.leaseStatusCode === '1' || formData.leaseTypeCode === '1' || formData.leaseTypeCode === '2'} />
                                </table>
                            </td>
                            <td className='unit-table-rates-dates'>
                                <label className='label-title'>{t('headings.rates')}</label>
                                <table className='unit-table half-border' width="100%" role="none">
                                    <Rate formData={formData} handleCurrencyChange={handleCurrencyChange} errors={errors} disabled={formData.leaseStatusCode === '1'} showAsterisk={formData.leaseStatusCode === '1' ? false : true} />
                                </table>
                                <br />
                                <table className='unit-table half-border' width="100%" role="none">
                                    <tbody>
                                        <tr>
                                            <td className='label-cell padding-left-10 padding-right-10'>
                                                {t('headings.rent-free-months')}
                                            </td>
                                            <td className="currency-input-td">
                                                <AIRPNumberInput
                                                    className={freeRentMonthClassName}
                                                    textAlign='right'
                                                    decimals={false}
                                                    name="freeRentMonth"
                                                    initialValue={formData.freeRentMonth as string}
                                                    tooltip={t('tooltips.rent-free-months-tooltip')} />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <br />
                                <label className='label-title' title={t('tooltips.step-dates-tooltip')}>{t('headings.step-dates')}</label>
                                <table className='unit-table half-border' role="none">
                                    <StepDate formData={formData} currencySymbol={currencyPerSF}
                                        handleCurrencyChange={handleCurrencyChange} handleDateChange={handleDateChange} errors={errors} disabled={formData.leaseStatusCode === '1'} />
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className='btn-grp'>
                    {!isReadOnly && <Button type='submit' testId='commercial-unit-save-button'>
                        {t("save", { ns: 'common' }).toLocaleUpperCase(i18n.language)}
                    </Button>}
                    {props.showDeleteBtn && (<Button type='danger' onClick={() => props.onCancel()} testId='commercial-unit-delete-button'>
                        {t("delete", { ns: 'common' }).toLocaleUpperCase(i18n.language)}
                    </Button>)}
                </div>
            </form >
        </div>
    );
}
